/**
 * Vue Router Configuration
 *
 * Replaces Alpine's manual section toggling with real routing.
 * Previously: Alpine.store('app').navigateTo() just changed currentSection
 * Now: Proper browser history, back/forward buttons, bookmarkable URLs
 */

import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

// ==================== ROUTE DEFINITIONS ====================

const routes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/views/Home.vue'),
    meta: {
      title: 'HummingBird Analytics - Property Tax Appeal Service'
    }
  },
  {
    path: '/sign-up',
    name: 'SignUp',
    component: () => import('@/views/SignUp.vue'),
    meta: {
      title: 'Sign Up - HummingBird Analytics',
      requiresGuest: true  // Only accessible when NOT signed in
    }
  },
  {
    path: '/sign-in',
    name: 'SignIn',
    component: () => import('@/views/SignIn.vue'),
    meta: {
      title: 'Sign In - HummingBird Analytics',
      requiresGuest: true
    }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: {
      title: 'Forgot Password - HummingBird Analytics',
      requiresGuest: true
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('@/views/ResetPassword.vue'),
    meta: {
      title: 'Reset Password - HummingBird Analytics'
    },
    // Validate token exists in query params
    beforeEnter: (to, from, next) => {
      if (to.query.token) {
        next()
      } else {
        // No token, redirect to forgot password
        next({ name: 'ForgotPassword' })
      }
    }
  },
  {
    path: '/password-reset-sent',
    name: 'PasswordResetSent',
    component: () => import('@/views/PasswordResetSent.vue'),
    meta: {
      title: 'Reset Link Sent - HummingBird Analytics'
    }
  },
  {
    path: '/my-appeal',
    name: 'MyAppeal',
    component: () => import('@/views/MyAppeal.vue'),
    meta: {
      title: 'My Appeal - HummingBird Analytics'
      // Note: Component shows sign-in prompt if not authenticated
      // Purchase status is checked within the component
    }
  },
  {
    path: '/comp-summary',
    name: 'CompSummary',
    component: () => import('@/views/CompSummary.vue'),
    meta: {
      title: 'Comp Assessment Summary - HummingBird Analytics'
      // Note: Component handles auth and purchase checks internally
    }
  },
  {
    path: '/eligibility-warning',
    name: 'EligibilityWarning',
    component: () => import('@/views/EligibilityWarning.vue'),
    meta: {
      title: 'Purchase Advisory - HummingBird Analytics'
    }
  },
  {
    path: '/payment-success',
    name: 'PaymentSuccess',
    component: () => import('@/views/PaymentSuccess.vue'),
    meta: {
      title: 'Payment Successful - HummingBird Analytics'
    }
  },
  {
    path: '/payment-cancelled',
    name: 'PaymentCancelled',
    component: () => import('@/views/PaymentCancelled.vue'),
    meta: {
      title: 'Payment Cancelled - HummingBird Analytics'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/views/Privacy.vue'),
    meta: {
      title: 'Privacy Policy - HummingBird Analytics'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: () => import('@/views/Contact.vue'),
    meta: {
      title: 'Contact Us - HummingBird Analytics'
    }
  },
  // Catch-all 404
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: '404 - Page Not Found'
    }
  }
]

// ==================== ROUTER INSTANCE ====================

const router = createRouter({
  history: createWebHistory(),
  routes,

  // Scroll behavior
  scrollBehavior(to, from, savedPosition) {
    // If navigating back/forward, restore scroll position
    if (savedPosition) {
      return savedPosition
    }

    // For auth pages (full-screen forms), don't scroll
    if (['SignUp', 'SignIn', 'ForgotPassword', 'ResetPassword'].includes(to.name)) {
      return { top: 0 }
    }

    // For other pages, smooth scroll to top
    return { top: 0, behavior: 'smooth' }
  }
})

// ==================== NAVIGATION GUARDS ====================

/**
 * Global Before Guard
 * Runs before every navigation
 * Handles authentication and authorization checks
 */
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth) {
    // If not signed in, redirect to sign in immediately
    // (Session validation happens in App.vue on mount, not on every navigation)
    if (!authStore.isSignedIn) {
      next({ name: 'SignIn', query: { redirect: to.fullPath } })
      return
    }

    // User is signed in, allow navigation
    // Individual components can check purchase status if needed
  }

  // Check if route requires guest (not signed in)
  if (to.meta.requiresGuest && authStore.isSignedIn) {
    // Already signed in, redirect to home
    next({ name: 'Home' })
    return
  }

  // All checks passed, proceed
  next()
})

/**
 * Global After Guard
 * Runs after every navigation
 */
router.afterEach((to, from) => {
  // Log navigation in development
  if (import.meta.env.DEV) {
    console.log(`[Router] Navigated from ${from.name} to ${to.name}`)
  }
})

// ==================== EXPORT ====================

export default router
